import type { FormDto } from "../../types/form";
import type { FieldDto } from "../../types/field";
import type { SectionDto } from "../../types/section";
import { serializeFieldDescription } from "./fieldDescription";
import { serializeFieldLabel } from "./fieldLabel";
import { normalizeSubmissionFieldId } from "./normalizeSubmission";

function countFieldsInSections(sections: SectionDto[]): number {
  return sections.reduce(
    (sum, section) => sum + (section.fields?.length ?? 0),
    0,
  );
}

function normalizeField(raw: Record<string, unknown>): FieldDto {
  return {
    id: normalizeSubmissionFieldId(raw.id) ?? undefined,
    form_id: Number(raw.form_id ?? raw.formId ?? 0),
    order: Number(raw.order ?? 0),
    label: serializeFieldLabel(raw.label),
    description: serializeFieldDescription(raw.description),
    name: String(raw.name ?? ""),
    type: String(raw.type ?? "text"),
    required: Boolean(raw.required),
    options: (raw.options as FieldDto["options"]) ?? null,
    visibility: (raw.visibility as FieldDto["visibility"]) ?? null,
    select: (raw.select as FieldDto["select"]) ?? null,
    fio: (raw.fio as FieldDto["fio"]) ?? null,
    city: (raw.city as FieldDto["city"]) ?? null,
    display: (raw.display as FieldDto["display"]) ?? null,
    date: (raw.date as FieldDto["date"]) ?? null,
    dateRange: (raw.dateRange as FieldDto["dateRange"]) ?? null,
    birthdate: (raw.birthdate as FieldDto["birthdate"]) ?? null,
    number: (raw.number as FieldDto["number"]) ?? null,
    yandexType:
      (raw.yandexType as string | null | undefined) ??
      (raw.yandex_type as string | null | undefined) ??
      null,
  };
}

function normalizeSection(raw: Record<string, unknown>): SectionDto {
  const rawFields = raw.fields ?? raw.form_fields ?? raw.formFields;
  const fields = Array.isArray(rawFields)
    ? rawFields.map((field) => normalizeField(field as Record<string, unknown>))
    : [];

  return {
    id: raw.id as number | undefined,
    form_id: Number(raw.form_id ?? raw.formId ?? 0),
    label: String(raw.label ?? ""),
    order: Number(raw.order ?? 0),
    created_at: raw.created_at as string | undefined,
    updated_at: raw.updated_at as string | undefined,
    fields,
  };
}

/** Ответ API: массив, `{ data: [...] }` или `{ sections: [...] }`. */
export function normalizeSectionsResponse(body: unknown): SectionDto[] {
  if (!body) return [];

  const rawItems = Array.isArray(body)
    ? body
    : typeof body === "object" && body !== null
      ? "data" in body && Array.isArray((body as { data?: unknown }).data)
        ? (body as { data: unknown[] }).data
        : "sections" in body &&
            Array.isArray((body as { sections?: unknown }).sections)
          ? (body as { sections: unknown[] }).sections
          : null
      : null;

  if (!rawItems) return [];

  return rawItems
    .filter((item) => item && typeof item === "object")
    .map((item) => normalizeSection(item as Record<string, unknown>));
}

/** Разделы с полями из `GET /forms/{id}` (если бэкенд вложил их туда). */
export function sectionsFromFormDto(form: FormDto): SectionDto[] {
  const sections = normalizeSectionsResponse(form.sections ?? []);

  if (countFieldsInSections(sections) > 0) {
    return sections;
  }

  const rootFields = form.fields ?? [];
  if (rootFields.length > 0) {
    return [
      {
        form_id: form.id,
        label: "Основные данные",
        order: 0,
        fields: rootFields,
      },
    ];
  }

  return sections;
}

/**
 * Поля живут в `GET /forms/{id}/sections`; в `GET /forms` sections часто пустые.
 * Берём источник с наибольшим числом полей.
 */
export function mergeFormSections(
  form: FormDto,
  sectionsFromApi: unknown = [],
): SectionDto[] {
  const fromForm = sectionsFromFormDto(form);
  const fromApi = normalizeSectionsResponse(sectionsFromApi);

  const formFieldCount = countFieldsInSections(fromForm);
  const apiFieldCount = countFieldsInSections(fromApi);

  if (apiFieldCount > 0) return fromApi;
  if (formFieldCount > 0) return fromForm;
  return fromApi.length > 0 ? fromApi : fromForm;
}

export function unwrapFormDto(body: unknown): FormDto {
  if (!body || typeof body !== "object") {
    throw new Error("Invalid form response");
  }

  const record = body as Record<string, unknown>;
  const raw =
    record.data && typeof record.data === "object" ? record.data : body;
  const form = raw as FormDto;

  if (form.sections) {
    form.sections = normalizeSectionsResponse(form.sections);
  }

  if (Array.isArray(form.fields) && form.fields.length > 0) {
    form.fields = form.fields.map((field) =>
      normalizeField(field as unknown as Record<string, unknown>),
    );
  }

  return form;
}
